import React, { FC } from 'react';

import PropTypes from 'prop-types';

import A from '@adsk/alloy-react-a';
import { OpenIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { SimpleModal } from '@adsk/alloy-react-modal';

import ACCImage from './assets/ACC_in-product.jpg';
import ACCLifecycle from './assets/Construction-Lifecycle.png';
import {
  HELP_LINK,
  LEARNING_CENTER_URL_1,
  LEARNING_CENTER_URL_2,
} from './helpers/const';
import {
  bodyStyle,
  contentSectionStyle,
  contentTitleStyle,
  footerHelpLinkStyle,
  footerHelpLinkTextStyle,
  footerSectionStyle,
  imageStyle,
  imageTextIntroductionStyle,
  imageTextDescriptionStyle,
  imageTextLinkStyle,
  learningCenterStyle,
} from './helpers/styles';
import { Offering, offeringPropType } from '../../helpers/types';
import TrialModalCarousel from '../TrialModalCarousel';
import TrialModalItem from '../TrialModalItem';

interface TrialModalProps {
  onClose: () => void;
  show: boolean;
  userTrials: Offering[];
}

const TrialModal: FC<TrialModalProps> = ({ onClose, show, userTrials }) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const getBannerContent = () => [
    <div css={learningCenterStyle} key="content-1">
      <div>
        <div css={imageTextIntroductionStyle}>
          {T.translate('MODAL_IMAGE_TEXT_INTRODUCTION_1')}
        </div>
        <div css={imageTextDescriptionStyle}>
          {T.translate('MODAL_IMAGE_TEXT_DESCRIPTION_1')}
        </div>
        <A
          style={imageTextLinkStyle}
          href={LEARNING_CENTER_URL_1}
          target="_blank"
        >
          {T.translate('MODAL_IMAGE_TEXT_LINK')}
        </A>
      </div>
      <img style={imageStyle} src={ACCLifecycle} />
    </div>,
    <div css={learningCenterStyle} key="content-0">
      <div>
        <div css={imageTextIntroductionStyle}>
          {T.translate('MODAL_IMAGE_TEXT_INTRODUCTION_2')}
        </div>
        <div css={imageTextDescriptionStyle}>
          {T.translate('MODAL_IMAGE_TEXT_DESCRIPTION_2')}
        </div>
        <A
          style={imageTextLinkStyle}
          href={LEARNING_CENTER_URL_2}
          target="_blank"
        >
          {T.translate('MODAL_IMAGE_TEXT_LINK')}
        </A>
      </div>
      <img style={imageStyle} src={ACCImage} />
    </div>,
  ];

  return (
    <SimpleModal
      title={T.translate('MODAL_TITLE')?.toString()}
      onClose={() => {
        onClose();
      }}
      show={show}
      modalBodyProps={{ style: bodyStyle }}
    >
      <TrialModalCarousel>{getBannerContent()}</TrialModalCarousel>
      <div css={contentSectionStyle}>
        <div css={contentTitleStyle}>{T.translate('MODAL_CONTENT_TITLE')}</div>
        {userTrials.map((userTrial) => (
          <TrialModalItem
            key={userTrial.offeringCategory}
            userTrial={userTrial}
          />
        ))}
      </div>
      <div css={footerSectionStyle}>
        <A style={footerHelpLinkStyle} href={HELP_LINK} target="_blank">
          <div css={footerHelpLinkTextStyle}>
            {T.translate('MODAL_FOOTER_HELP_LINK')}
          </div>
          <OpenIcon />
        </A>
      </div>
    </SimpleModal>
  );
};

TrialModal.displayName = 'TrialModal';
TrialModal.propTypes = {
  /** Handler for close modal event */
  onClose: PropTypes.func.isRequired,
  /**  Flag to display modal */
  show: PropTypes.bool.isRequired,
  /** User Trials data to display */
  userTrials: PropTypes.arrayOf(offeringPropType.isRequired).isRequired,
};
export default TrialModal;
